/**
 * Position Metrics Card
 *
 * Compact display of position financial metrics in a 2-column grid.
 * Shows current value, avg cost, current price, unrealized P&L, realized P&L.
 * With edit button to navigate to position edit screen.
 */

import React, { useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { Card } from '../ui/Card';
import type { PortfolioNavigatorParamList } from '../../navigation/PortfolioNavigator';

interface PositionMetricsCardProps {
  position: {
    id: number;
    symbol: string;
    quantity: string | number;
    cost_price: string | number;
    close_price: string | number | null;
    close_price_date?: string | null;
    value: string | number;
    unrealized_pl: string | number;
    realized_pl?: string | number | null;
    currency: string;
  };
}

type PositionMetricsNavigationProp = StackNavigationProp<
  PortfolioNavigatorParamList,
  'AddPosition'
>;

export function PositionMetricsCard({ position }: PositionMetricsCardProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const navigation = useNavigation<PositionMetricsNavigationProp>();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const handleEditPress = () => {
    navigation.navigate('AddPosition', { positionId: position.id });
  };

  const quantity = parseFloat(String(position.quantity));
  const costPrice = parseFloat(String(position.cost_price));
  const closePrice = position.close_price ? parseFloat(String(position.close_price)) : null;
  const value = parseFloat(String(position.value));
  const unrealizedPL = parseFloat(String(position.unrealized_pl));
  const realizedPL = position.realized_pl ? parseFloat(String(position.realized_pl)) : 0;

  const formatCurrency = (amount: number | null) => {
    if (amount === null) return 'N/A';
    return `${position.currency} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getPLStyle = (pl: number) => ({
    color: pl >= 0 ? theme.colors.success : theme.colors.error,
  });

  const getPriceChange = () => {
    if (!closePrice || !costPrice) return null;
    const change = ((closePrice - costPrice) / costPrice) * 100;
    return change;
  };

  const priceChange = getPriceChange();

  return (
    <Card style={styles.container}>
      {/* Header with Edit Button */}
      <View style={styles.headerContainer}>
        <Text style={[styles.header, { color: theme.colors.text }]}>
          {t('portfolio.positionDetails.financialMetrics', { defaultValue: 'Financial Metrics' })}
        </Text>
        <TouchableOpacity
          onPress={handleEditPress}
          style={[styles.editButton, { backgroundColor: theme.colors.backgroundElevated }]}
          activeOpacity={0.7}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <Ionicons name="create-outline" size={16} color={theme.colors.primary} />
          <Text style={[styles.editButtonText, { color: theme.colors.primary }]}>
            {t('common.edit', { defaultValue: 'Edit' })}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Metrics Grid */}
      <View style={styles.grid}>
        {/* Quantity */}
        <View style={styles.metricItem}>
          <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
            {t('portfolio.positionDetails.quantity', { defaultValue: 'Quantity' })}
          </Text>
          <Text style={[styles.value, { color: theme.colors.text }]}>
            {quantity.toLocaleString('en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 8,
            })}
          </Text>
        </View>

        {/* Current Value */}
        <View style={styles.metricItem}>
          <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
            {t('portfolio.positionDetails.currentValue', { defaultValue: 'Current Value' })}
          </Text>
          <Text style={[styles.value, { color: theme.colors.text }]}>{formatCurrency(value)}</Text>
        </View>

        {/* Avg Cost */}
        <View style={styles.metricItem}>
          <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
            {t('portfolio.positionDetails.avgCost', { defaultValue: 'Avg Cost' })}
          </Text>
          <Text style={[styles.value, { color: theme.colors.text }]}>
            {formatCurrency(costPrice)}
          </Text>
        </View>

        {/* Current Price */}
        <View style={styles.metricItem}>
          <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
            {t('portfolio.positionDetails.currentPrice', { defaultValue: 'Current Price' })}
          </Text>
          <View>
            <View style={styles.valueWithChange}>
              <Text style={[styles.value, { color: theme.colors.text }]}>
                {formatCurrency(closePrice)}
              </Text>
              {priceChange !== null && (
                <Text style={[styles.priceChange, getPLStyle(priceChange)]}>
                  {priceChange >= 0 ? '+' : ''}
                  {priceChange.toFixed(1)}%
                </Text>
              )}
            </View>
            {position.close_price_date && (
              <Text style={[styles.dateText, { color: theme.colors.textSecondary }]}>
                {formatDate(position.close_price_date)}
              </Text>
            )}
          </View>
        </View>

        {/* Unrealized P&L */}
        <View style={styles.metricItem}>
          <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
            {t('portfolio.positionDetails.unrealizedPL', { defaultValue: 'Unrealized P/L' })}
          </Text>
          <View style={styles.valueWithChange}>
            <Text style={[styles.value, getPLStyle(unrealizedPL)]}>
              {formatCurrency(unrealizedPL)}
            </Text>
            {costPrice > 0 && quantity > 0 && (
              <Text style={[styles.priceChange, getPLStyle(unrealizedPL)]}>
                ({((unrealizedPL / (costPrice * quantity)) * 100).toFixed(1)}%)
              </Text>
            )}
          </View>
        </View>

        {/* Realized P&L */}
        <View style={styles.metricItem}>
          <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
            {t('portfolio.positionDetails.realizedPL', { defaultValue: 'Realized P/L' })}
          </Text>
          <Text style={[styles.value, getPLStyle(realizedPL)]}>{formatCurrency(realizedPL)}</Text>
        </View>
      </View>
    </Card>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      marginHorizontal: 12,
      marginTop: 12,
      padding: 12,
    },
    headerContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 12,
    },
    header: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    editButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: theme.radii.sm || 6,
    },
    editButtonText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
      marginLeft: 4,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    grid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginHorizontal: -4,
    },
    metricItem: {
      width: '50%',
      paddingHorizontal: 4,
      paddingVertical: 6,
      minHeight: 56,
    },
    label: {
      fontSize: theme.typography.fontSizeXs,
      marginBottom: 2,
    },
    value: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    valueWithChange: {
      flexDirection: 'row',
      alignItems: 'baseline',
      flexWrap: 'wrap',
    },
    priceChange: {
      fontSize: theme.typography.fontSizeXs,
      marginLeft: 4,
    },
    dateText: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: 2,
      fontStyle: 'italic',
    },
  });
}
